import { useState, useEffect } from 'react';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { MessageSquare, CheckCircle, Clock, AlertTriangle, Zap, Filter, X, ChevronDown, ChevronUp, Send } from 'lucide-react';

const TYPE_LABELS = { issue:'🔧 Issue', material_request:'📦 Material Request', clarification:'❓ Clarification', other:'💬 Other' };
const STATUS_COLORS = { open:'bg-red-100 text-red-700', in_review:'bg-yellow-100 text-yellow-700', resolved:'bg-green-100 text-green-700', closed:'bg-gray-100 text-gray-600' };
const PRIORITY_COLORS = { urgent:'bg-red-500 text-white', high:'bg-orange-500 text-white', medium:'bg-yellow-500 text-white', low:'bg-gray-200 text-gray-700' };

export default function AdminQueriesPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('queries');
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [responseStatus, setResponseStatus] = useState('resolved');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const res = await api.get('/queries', { params });
      setQueries(res.data);
    } catch { toast.error('Load failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterStatus]);

  const handleRespond = async (id) => {
    if (!responseText.trim()) return toast.error('Response Mandatory');
    try {
      await api.patch(`/queries/${id}/respond`, { admin_response: responseText, status: responseStatus });
      toast.success('Response sent!');
      setRespondingTo(null);
      setResponseText('');
      load();
    } catch { toast.error('Failed'); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/queries/${id}/status`, { status });
      toast.success('Status updated');
      load();
    } catch { toast.error('Failed'); }
  };

  const openCount = queries.filter(q => q.status === 'open').length;
  const urgentCount = queries.filter(q => q.priority === 'urgent' && q.status === 'open').length;
  const resolvedCount = queries.filter(q => q.status === 'resolved').length;

  const formatTime = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    const now = new Date();
    const diff = now - d;
    const hrs = Math.floor(diff / 3600000);
    if (hrs < 1) return Math.floor(diff/60000) + 'm ago';
    if (hrs < 24) return hrs + 'h ago';
    return d.toLocaleDateString('en-IN', { day:'2-digit', month:'short' });
  };

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">📋 Worker Queries & Issues</h1>
        <p className="text-gray-500 text-sm mt-1">Worker problems, material requests and clarifications</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-center cursor-pointer hover:bg-red-100" onClick={() => setFilterStatus('open')}>
          <p className="text-3xl font-bold text-red-600">{openCount}</p>
          <p className="text-sm text-red-500 mt-1">Open Queries</p>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 text-center">
          <p className="text-3xl font-bold text-orange-600">{urgentCount}</p>
          <p className="text-sm text-orange-500 mt-1">Urgent / High</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-center cursor-pointer hover:bg-green-100" onClick={() => setFilterStatus('resolved')}>
          <p className="text-3xl font-bold text-green-600">{resolvedCount}</p>
          <p className="text-sm text-green-500 mt-1">Resolved</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {[['','All'],['open','Open'],['in_review','In Review'],['resolved','Resolved'],['closed','Closed']].map(([v,l]) => (
          <button key={v} onClick={() => setFilterStatus(v)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              filterStatus === v ? 'bg-amber-500 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Query List */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : queries.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400">Koi query nahi hai — achchi baat hai! 🎉</p>
        </div>
      ) : (
        <div className="space-y-3">
          {queries.map(q => {
            const isExpanded = expandedId === q.id;
            const isResponding = respondingTo === q.id;
            return (
              <div key={q.id} className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-all ${
                q.status === 'open' ? 'border-red-200' :
                q.status === 'in_review' ? 'border-yellow-200' :
                q.status === 'resolved' ? 'border-green-200' : 'border-gray-100'}`}>
                {/* Header */}
                <div className="p-4 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : q.id)}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_COLORS[q.priority]}`}>
                          {q.priority}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[q.status]}`}>
                          {q.status}
                        </span>
                        <span className="text-xs text-gray-500">{TYPE_LABELS[q.query_type]}</span>
                        <span className="text-xs text-gray-400 ml-auto">{formatTime(q.created_at)}</span>
                      </div>
                      <p className="font-semibold text-gray-900 text-sm">{q.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        by <strong>{q.raised_by_name}</strong>
                        {q.dept_name && <> · {q.dept_name}</>}
                        {q.proto_code && <> · {q.proto_code}</>}
                        {q.project_name && <> · {q.project_name}</>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {q.status === 'open' && (
                        <button onClick={e => { e.stopPropagation(); setRespondingTo(q.id); setExpandedId(q.id); }}
                          className="bg-blue-500 text-white text-xs px-3 py-1.5 rounded-lg font-medium hover:bg-blue-600 flex items-center gap-1">
                          <Send className="w-3 h-3" /> Respond
                        </button>
                      )}
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-100 p-4 space-y-3">
                    {/* Description */}
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-500 font-medium mb-1">Worker ki baat:</p>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{q.description}</p>
                    </div>

                    {q.images?.length > 0 && (
                      <div className="bg-white border border-gray-100 rounded-xl p-3">
                        <p className="text-xs text-gray-500 font-medium mb-1">MR images:</p>
                        <div className="grid grid-cols-3 gap-2">
                          {q.images.map(img => (
                            <img key={img.id} src={img.image_url} alt="query" className="w-full h-20 object-cover rounded-lg" />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Task info */}
                    {q.task_title && (
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Clock className="w-3.5 h-3.5" />
                        Task: <span className="text-gray-700 font-medium">{q.task_title}</span>
                      </div>
                    )}

                    {/* Admin response (if exists) */}
                    {q.admin_response && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                        <p className="text-xs text-green-600 font-medium mb-1">✅ Admin Response ({q.responded_by_name}):</p>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{q.admin_response}</p>
                        <p className="text-xs text-gray-400 mt-1">{formatTime(q.responded_at)}</p>
                      </div>
                    )}

                    {/* Status change buttons */}
                    {q.status !== 'closed' && (
                      <div className="flex gap-2 flex-wrap">
                        {q.status !== 'in_review' && (
                          <button onClick={() => updateStatus(q.id, 'in_review')}
                            className="text-xs px-3 py-1.5 border border-yellow-300 text-yellow-700 rounded-lg hover:bg-yellow-50">
                            Mark In Review
                          </button>
                        )}
                        {q.status !== 'resolved' && (
                          <button onClick={() => updateStatus(q.id, 'resolved')}
                            className="text-xs px-3 py-1.5 border border-green-300 text-green-700 rounded-lg hover:bg-green-50">
                            Mark Resolved
                          </button>
                        )}
                        <button onClick={() => updateStatus(q.id, 'closed')}
                          className="text-xs px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50">
                          Close
                        </button>
                      </div>
                    )}

                    {/* Respond form */}
                    {isResponding && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-2">
                        <p className="text-xs font-medium text-blue-700">Response likhiye:</p>
                        <textarea value={responseText} onChange={e => setResponseText(e.target.value)}
                          rows={3} placeholder="Worker ko jawab do..."
                          className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white" />
                        <div className="flex items-center gap-2">
                          <select value={responseStatus} onChange={e => setResponseStatus(e.target.value)}
                            className="border border-blue-200 rounded-lg px-2 py-1.5 text-xs bg-white">
                            <option value="resolved">Mark Resolved</option>
                            <option value="in_review">Keep In Review</option>
                            <option value="closed">Close Query</option>
                          </select>
                          <div className="flex gap-2 ml-auto">
                            <button onClick={() => setRespondingTo(null)} className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
                            <button onClick={() => handleRespond(q.id)} className="text-xs px-3 py-1.5 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 flex items-center gap-1">
                              <Send className="w-3 h-3" /> Send
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  
    </PermissionGuard>
  );
}
